import { getListFromApiResponse } from "../utils/apiListMappers";

/**
 * Normalize one user record from API (list row or GET /users/:id).
 */
export function mapUserListItemFromApi(u) {
  if (!u || typeof u !== "object") return null;
  const name =
    [u.firstName, u.lastName].filter(Boolean).join(" ").trim() ||
    String(u.name ?? "").trim() ||
    String(u.fullName ?? "").trim() ||
    String(u.email ?? u.userEmail ?? u.primaryEmail ?? "").trim() ||
    "";
  const roleRaw = u.role;
  const roleIsObject = typeof roleRaw === "object" && roleRaw != null;
  const roleDisplay = roleIsObject && roleRaw.name != null ? roleRaw.name : roleRaw;
  const email =
    String(
      u.email ??
        u.userEmail ??
        u.primaryEmail ??
        u.contactEmail ??
        u.emailAddress ??
        u.mail ??
        (typeof u.profile === "object" && u.profile != null ? u.profile.email : undefined) ??
        ""
    ).trim() || undefined;
  const teamIdRaw =
    u.teamId ??
    u.defaultTeamId ??
    (typeof u.team === "object" ? u.team?.id : undefined) ??
    u.team_id ??
    (Array.isArray(u.teams) && u.teams[0] && typeof u.teams[0] === "object"
      ? u.teams[0].id
      : undefined);
  const teamId =
    teamIdRaw != null && String(teamIdRaw).trim() !== "" ? String(teamIdRaw).trim() : undefined;
  const emailVerified = u.email_verified ?? u.emailVerified ?? u.isEmailVerified;
  const mustChangePassword = u.must_change_password ?? u.mustChangePassword;
  return {
    ...u,
    ...(email ? { email } : {}),
    email_verified: emailVerified,
    emailVerified,
    must_change_password: mustChangePassword,
    mustChangePassword,
    name,
    role: roleDisplay,
    roleId: u.roleId ?? (roleIsObject ? roleRaw?.id : undefined),
    teamId,
    teamName:
      typeof u.team === "object" && u.team !== null && u.team?.name != null
        ? u.team.name
        : typeof u.team === "string"
          ? u.team
          : u.teamName,
    status:
      u.is_active !== undefined
        ? u.is_active === 1 || u.is_active === true
          ? "Active"
          : "Inactive"
        : u.status,
  };
}

/**
 * Unwrap GET /users/:id response bodies (flat user, `{ data }`, or nested `{ data: { data } }`).
 */
export function getUserFromByIdResponse(payload) {
  if (payload == null || typeof payload !== "object") return null;
  let raw = payload;
  if (payload.data != null && typeof payload.data === "object") {
    raw = payload.data;
    if (raw.data != null && typeof raw.data === "object" && !Array.isArray(raw.data)) {
      raw = raw.data;
    }
  }
  return mapUserListItemFromApi(raw);
}

/** Merge unique users from one or more raw API payloads (GET /users, GET /users/all). */
export function mergeUsersListFromApiPayloads(...payloads) {
  const byKey = new Map();
  for (const payload of payloads) {
    if (payload == null) continue;
    const list = getListFromApiResponse(payload)
      .map((u) => mapUserListItemFromApi(u))
      .filter(Boolean);
    for (const user of list) {
      const key =
        user.id != null
          ? String(user.id)
          : user.userId != null
            ? String(user.userId)
            : resolveUserEmailFromRecord(user);
      if (!key) continue;
      byKey.set(key, user);
    }
  }
  return Array.from(byKey.values());
}

/** Resolve email from a mapped or raw user row. */
export function resolveUserEmailFromRecord(user) {
  if (!user || typeof user !== "object") return "";
  const mapped = mapUserListItemFromApi(user);
  if (!mapped) return "";
  return String(
    mapped.email ??
      mapped.userEmail ??
      mapped.primaryEmail ??
      mapped.contactEmail ??
      mapped.emailAddress ??
      mapped.mail ??
      ""
  ).trim();
}

/** Display name for user picker rows. */
export function resolveUserDisplayName(user) {
  if (!user || typeof user !== "object") return "Unknown";
  const mapped = mapUserListItemFromApi(user) ?? user;
  return (
    mapped.name ||
    [mapped.firstName, mapped.lastName].filter(Boolean).join(" ").trim() ||
    resolveUserEmailFromRecord(mapped) ||
    String(mapped.id ?? mapped.userId ?? "Unknown")
  );
}

/**
 * Derive and map users list from raw API response.
 * Supports the same list shapes as getListFromApiResponse.
 */
export function getUsersListFromApiData(data) {
  return mergeUsersListFromApiPayloads(data);
}

/**
 * Resolve a user for edit without GET /users/:id — search cached list responses by id.
 * Checks GET /users/all (`allListData`) then paginated GET /users (`data`).
 */
export function findUserInCachedUsersPayloads(allListData, paginatedData, id) {
  if (id == null || String(id).trim() === "") return null;
  const idStr = String(id);
  for (const payload of [allListData, paginatedData]) {
    if (payload == null) continue;
    const list = getUsersListFromApiData(payload);
    const found = list.find((u) => u && String(u.id) === idStr);
    if (found) return found;
  }
  return null;
}

/** Get meta from raw API response. API shape: { data: { data: [], meta } } or flat list */
export function getUsersMetaFromApiData(data) {
  const d = data?.data;
  if (d && typeof d === "object" && !Array.isArray(d) && d.meta != null) return d.meta;
  return null;
}

/**
 * Map UI role label to API `role` query value for GET /users.
 * Prefer `code` / `key` / `slug` from GET /users/roles when present; else legacy name map; else SCREAMING_SNAKE.
 * @param {string} value - Selected role display name from filter
 * @param {Array<{ id?: string, name?: string, code?: string, key?: string, slug?: string }>} [rolesFromApi] - Roles from fetchUserRoles
 */
export function toUserRoleApi(value, rolesFromApi = []) {
  if (!value || String(value).trim() === "") return undefined;
  const v = String(value).trim();
  const match = rolesFromApi.find((r) => r && String(r.name ?? "").trim() === v);
  const code = match?.code ?? match?.key ?? match?.slug ?? match?.roleCode ?? match?.value;
  if (code != null && String(code).trim() !== "") {
    return String(code).trim();
  }
  const map = {
    Admin: "ADMIN",
    "Team Leader": "TEAM_LEADER",
    "Project Leader": "PROJECT_LEADER",
    "Regular User": "REGULAR_USER",
    Staff: "STAFF",
    Vendor: "VENDOR",
  };
  return map[v] || String(v).toUpperCase().replace(/\s+/g, "_");
}

/**
 * Map Status filter UI value → GET /users `is_active` query (1/0).
 * Omit param when "All" / empty.
 */
export function toUserIsActiveApi(statusLabel) {
  if (statusLabel == null || String(statusLabel).trim() === "") return undefined;
  const s = String(statusLabel).trim();
  if (s === "All") return undefined;
  if (s === "1" || s.toLowerCase() === "active") return 1;
  if (s === "0" || s.toLowerCase() === "inactive") return 0;
  return undefined;
}
